<template>
  <div v-if="state !== 'idle'" class="a2p-scan-result" :class="state">
    <!-- Checking -->
    <div v-if="state === 'checking'" class="scan-line">
      <span class="scan-spinner" />
      <div class="scan-body">
        <span>{{ checkingLabel }}</span>
        <!-- Shown once the check has been running a couple seconds: some sites
             are client-rendered (React/Vue/etc.) and we load the page fully in a
             real browser, which takes a few extra seconds. Reassures the user it
             isn't stuck.
             This copy deliberately makes NO claim that client-rendered pages are
             safe. They are not: we render in order to scan, but the carrier does
             not render at all, so a JS-only policy page reads as blank to them.
             An earlier version reassured the user here and that is precisely how
             Family EcoDental lost 18 days. See the jsOnlyContent branch below. -->
        <div v-if="slowCheck" class="scan-sub">
          Client-rendered (JavaScript) sites take a few extra seconds to load.
        </div>
      </div>
    </div>

    <!-- Error (network / timeout / unreachable) -->
    <div v-else-if="state === 'error'" class="scan-line">
      <span class="scan-icon err">✗</span>
      <div class="scan-body">
        <div class="scan-headline">We couldn't reach this URL</div>
        <div class="scan-sub">{{ errorMessage }}</div>
        <button type="button" class="scan-retry" @click="$emit('retry')">Re-check</button>
      </div>
    </div>

    <!-- Website reachable (urlType='website').
         Reachable is NOT the same as readable: an unrendered single-page-app
         homepage answers HTTP 200 with an empty body. Family EcoDental's served
         66 characters and was rejected 30908 — "a compliant privacy policy can
         not be verified" — with both policy pages already perfect, because the
         reviewer could not read the site the policy belonged to. (2026-08-31.) -->
    <div v-else-if="state === 'website-ok'">
      <div v-if="result.isShell" class="scan-jsonly">
        <div class="scan-line">
          <span class="scan-icon err">✗</span>
          <strong>Your website only works with JavaScript enabled.</strong>
        </div>
        <div class="scan-sub">
          It looks fine in a browser, but the carriers read it <em>without</em> running
          JavaScript — and they see
          {{ result.rawTextLength }} character{{ result.rawTextLength === 1 ? '' : 's' }}
          of text instead of your homepage. They cannot confirm your practice is real,
          so they reject the registration even when your privacy and terms pages are correct.
          <br />
          Ask whoever maintains your website to serve the homepage as static HTML, and to
          link your privacy policy and terms from it. To verify: open your site, right-click
          and choose <em>View Page Source</em> — your practice name, address and services
          must appear there.
        </div>
        <button type="button" class="scan-retry" @click="$emit('retry')">Re-check</button>
      </div>
      <div v-else class="scan-line">
        <span class="scan-icon ok">✓</span>
        <span>Your website loads — carriers can reach it.</span>
      </div>
    </div>

    <!-- Privacy/Terms: page reachable + scan results -->
    <div v-else-if="state === 'page-scan'" class="scan-body full">
      <div class="scan-line">
        <span class="scan-icon" :class="result.passesCompliance ? 'ok' : 'warn'">
          {{ result.passesCompliance ? '✓' : '⚠' }}
        </span>
        <strong>{{ pageLabel }}</strong>
        <span v-if="result.passesCompliance" class="scan-sub-inline">contains all required SMS language.</span>
        <span v-else class="scan-sub-inline">
          is missing {{ result.missingTerms.length }} required item{{ result.missingTerms.length === 1 ? '' : 's' }}.
        </span>
      </div>
      <ul v-if="result.foundTerms && result.foundTerms.length" class="scan-terms-list found">
        <li v-for="t in result.foundTerms" :key="'f-' + t">
          <span class="mark ok">✓</span> {{ t }}
        </li>
      </ul>
      <ul v-if="result.missingTerms && result.missingTerms.length" class="scan-terms-list missing">
        <li v-for="t in result.missingTerms" :key="'m-' + t">
          <span class="mark err">✗</span> {{ t }}
        </li>
      </ul>

      <!-- JS-only content — BLOCKING. The page reads fine in a browser but a
           carrier reviewer fetches it without running JavaScript and sees a
           blank shell. Family EcoDental lost 18 days to exactly this: our own
           scanner passed the page twice because it renders before scanning. -->
      <div v-if="result.jsOnlyContent" class="scan-jsonly">
        <div class="scan-line">
          <span class="scan-icon err">✗</span>
          <strong>This page only works with JavaScript enabled.</strong>
        </div>
        <div class="scan-sub">
          It looks correct in a browser, but the carriers read the page
          <em>without</em> running JavaScript — so they see a blank page and reject
          the registration.
          <span v-if="result.jsOnlyLostTerms && result.jsOnlyLostTerms.length">
            Invisible to them: {{ result.jsOnlyLostTerms.join('; ') }}.
          </span>
        </div>
        <div class="scan-sub">
          <strong>Fix:</strong> publish this policy as a plain static HTML page
          rather than a page your site builds in the browser, then point this URL
          at it. To verify, open the page and use <strong>View Page Source</strong> —
          the policy text must appear in the source.
        </div>
      </div>

      <!-- Non-blocking advice (2026-09-15). Brester Dentistry was rejected with a privacy
           policy that only said phone numbers are "not shared with third parties for
           marketing purposes"; carriers want it to say consent is not shared with
           affiliates either. Campaigns HAVE been approved without it, so this never turns
           the result red and never disables Continue — it tells them what to add. -->
      <div
        v-for="(w, i) in scanWarnings"
        :key="'w-' + i"
        class="scan-warning"
        data-test="a2p-scan-warning"
      >
        <div class="scan-line">
          <span class="scan-icon warn">⚠</span>
          <strong v-if="w.code === 'consent_data_sharing'">Recommended: say that consent is never shared with affiliates.</strong>
          <strong v-else>Recommended</strong>
        </div>
        <template v-if="w.code === 'consent_data_sharing'">
          <div class="scan-sub">
            Your privacy policy doesn't clearly say that patients' phone numbers and their
            permission to receive texts are never shared with other companies, including
            affiliates. Carriers have turned registrations down for this. Add this sentence
            to the text messaging section of your privacy policy:
          </div>
          <blockquote class="scan-warning-sentence" data-test="a2p-scan-warning-sentence">{{ w.sentence }}</blockquote>
        </template>
        <div v-else class="scan-sub">{{ w.message }}</div>
      </div>

      <button type="button" class="scan-retry" @click="$emit('retry')">Re-check now</button>
    </div>
  </div>
</template>

<script>
// After this long in the "checking" state, show the render-delay reassurance.
// Server-rendered scans return in well under a second; a client-rendered (SPA)
// page triggers a headless-browser render on the backend that adds a few
// seconds — this keeps that wait from reading as a hang.
const SLOW_CHECK_MS = 2500;

export default {
  name: 'A2PUrlScanResult',
  props: {
    // 'idle' | 'checking' | 'error' | 'website-ok' | 'page-scan'
    state: { type: String, required: true },
    urlType: { type: String, default: 'website' },
    result: { type: Object, default: () => ({}) },
    errorMessage: { type: String, default: '' },
  },
  data() {
    return { slowCheck: false };
  },
  watch: {
    state: {
      immediate: true,
      handler(val) {
        // Restart the slow-check timer whenever we (re-)enter 'checking';
        // clear it and reset the flag whenever we leave.
        this.clearSlowTimer();
        this.slowCheck = false;
        if (val === 'checking') {
          this._slowTimer = setTimeout(() => { this.slowCheck = true; }, SLOW_CHECK_MS);
        }
      },
    },
  },
  beforeDestroy() {
    this.clearSlowTimer();
  },
  computed: {
    checkingLabel() {
      if (this.slowCheck) {
        const slow = {
          website: 'Still loading your website…',
          privacy: 'Still scanning your privacy policy…',
          terms: 'Still scanning your terms of service…',
        };
        return slow[this.urlType] || 'Still checking…';
      }
      const phrase = {
        website: 'Checking your website…',
        privacy: 'Scanning your privacy policy for SMS compliance language…',
        terms: 'Scanning your terms of service for SMS compliance language…',
      };
      return phrase[this.urlType] || 'Checking…';
    },
    pageLabel() {
      const phrase = {
        website: 'Website',
        privacy: 'Your privacy policy',
        terms: 'Your terms of service',
      };
      return phrase[this.urlType] || 'Page';
    },
    // Server-provided advice. Tolerates an older backend (no field) and malformed items.
    scanWarnings() {
      const list = this.result && Array.isArray(this.result.warnings) ? this.result.warnings : [];
      return list.filter((w) => w && (
        (w.code === 'consent_data_sharing' && w.sentence) || w.message
      ));
    },
  },
  methods: {
    clearSlowTimer() {
      if (this._slowTimer) {
        clearTimeout(this._slowTimer);
        this._slowTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.a2p-scan-result {
  margin: 10px 0 0 36px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  border-left: 3px solid;
}
.a2p-scan-result.checking {
  background: var(--a2p-accent-soft);
  border-left-color: var(--a2p-accent);
  color: var(--a2p-accent-ink);
}
.a2p-scan-result.error {
  background: var(--a2p-danger-soft);
  border-left-color: var(--a2p-danger);
  color: var(--a2p-danger-ink);
}
.a2p-scan-result.website-ok {
  background: var(--a2p-success-soft);
  border-left-color: var(--a2p-success);
  color: var(--a2p-success-ink);
}
.a2p-scan-result.page-scan {
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-left: 3px solid var(--a2p-accent);
}

.scan-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
/* Long headings must wrap inside the flex row instead of spilling past the box. */
.scan-line > * {
  min-width: 0;
  overflow-wrap: anywhere;
}

.scan-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--a2p-accent-soft);
  border-top-color: var(--a2p-accent);
  border-radius: 50%;
  animation: a2p-scan-spin 0.8s linear infinite;
  margin-top: 2px;
}
@keyframes a2p-scan-spin {
  to { transform: rotate(360deg); }
}

.scan-icon {
  flex-shrink: 0;
  font-weight: 700;
  width: 16px;
  text-align: center;
  margin-top: 1px;
}
.scan-icon.ok  { color: var(--a2p-success-ink); }
.scan-icon.warn { color: var(--a2p-warn-ink); }
.scan-icon.err { color: var(--a2p-danger-ink); }

.scan-body {
  flex: 1;
}
.scan-body.full {
  display: block;
}
.scan-headline {
  font-weight: 600;
  color: var(--a2p-fg);
}
.scan-sub {
  color: var(--a2p-fg-2);
  margin-top: 2px;
}
.scan-sub-inline {
  color: var(--a2p-fg-2);
  font-weight: normal;
}

.scan-terms-list {
  list-style: none;
  margin: 10px 0 6px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.scan-terms-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: var(--a2p-fg-2);
}
.scan-terms-list .mark {
  width: 14px;
  font-weight: 700;
  flex-shrink: 0;
}
.scan-terms-list .mark.ok { color: var(--a2p-success-ink); }
.scan-terms-list .mark.err { color: var(--a2p-danger-ink); }

/* JS-only content — a blocking failure, so it gets the danger treatment rather
   than sitting quietly in the missing-terms list. The page "looks fine", which
   is exactly why this needs to be loud. */
.scan-jsonly {
  margin-top: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-left: 3px solid var(--a2p-danger-ink);
  background: var(--a2p-danger-bg, rgba(220, 38, 38, 0.06));
  border-radius: 0 4px 4px 0;
}
.scan-jsonly .scan-sub { margin-top: 0.4rem; }

/* Non-blocking advice — amber, never the danger treatment: the page still passes. */
.scan-warning {
  margin-top: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-left: 3px solid var(--a2p-warn);
  background: var(--a2p-warn-soft);
  color: var(--a2p-warn-ink);
  border-radius: 0 4px 4px 0;
  max-width: 100%;
}
.scan-warning .scan-sub { margin-top: 0.4rem; }
.scan-warning-sentence {
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.75rem;
  background: var(--a2p-surface, #fff);
  border: 1px solid var(--a2p-border, rgba(0, 0, 0, 0.1));
  border-radius: 4px;
  color: var(--a2p-fg);
  font-style: italic;
  overflow-wrap: anywhere;
  user-select: all;
}

.scan-retry {
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: transparent;
  border: 1px solid currentColor;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
}
.scan-retry:hover {
  background: rgba(0, 0, 0, 0.04);
}
/* Phones: drop the 36px gutter indent so the result and its warning use the full card width. */
@media (max-width: 600px) {
  .a2p-scan-result {
    margin-left: 0;
    padding: 10px;
  }
  .scan-warning {
    padding: 0.625rem;
  }
}
</style>
